import React, { Component } from "react";
import Strings from "@my-dld/assets/strings";
import Popup from "../Popup/Popup";
import {
	convertToGPropertyType,
	GreenListStatus,
	PropertyTypesItems,
	SubscriptionAll,
	SubscriptionService,
	SubscriptionServiceId
} from "../../greenlistapi/entities";
import { JQModal } from "../Popup/Utils";
import { IGreenListForm, IGreenListFormProps, IGreenListFormState } from "./views/Base";
import { SubscriptionServicesForm } from "./views/Subscriptions";
import { SubscriptionDetailsForm } from "./views/SubscriptionDetails";
import Terms, { ContactInfo } from "./views/Terms";
import Otp from "./views/OTP";
import { getEmail, getMobileNumber, getName, getParticipantID } from "@10x/sso";
import { normaliseMobileNumber } from "@eres/core/dist/StringUtils";
import { showErrorMessage } from "@my-dld/utils/UIUtils";

export type GreenListMode = "SUBSCRIBE" | "VIEW" | "UPDATE";

interface Props {
	mode: GreenListMode;
	onDismiss: () => void;
	onSubscriptionUpdated: ( subscription: GreenListStatus ) => Promise<any>;
	subscription?: GreenListStatus;
}

type ScreenView = "services" | "service_info" | "terms" | "otp";

interface State {
	view: ScreenView;
	subscriptions: SubscriptionService[];
	tempSubscription?: SubscriptionService;
	formValid?: boolean;
	nActionLabel: string;
	pActionLabel: string;
	requireOtp?: boolean;
	contactInfo?: ContactInfo;
	otpRequirement?: ContactInfo;
}

class GreenListPopup extends Component<Props, State> {
	form = React.createRef<IGreenListForm<IGreenListFormProps, IGreenListFormState>>();

	constructor( props: Props ) {
		super( props );
		const state: State = {
			view: "services",
			pActionLabel: this.getPositiveActionLabel( "services" ),
			nActionLabel: this.getNegativeActionLabel( "services" ),
			subscriptions: [],
		};
		const currentSubscription = props.subscription;
		if ( props.mode === "UPDATE" && currentSubscription ) {
			state.subscriptions = currentSubscription.ServiceTypes ?? [ SubscriptionAll ];
			state.formValid = true;
			state.contactInfo = { email: currentSubscription.Email, mobile: currentSubscription.Mobile };
		}
		this.state = state;
	}

	isOky = () => this.state.formValid;

	switchView = ( newView: ScreenView ) => {
		this.setState( {
			view: newView,
			pActionLabel: this.getPositiveActionLabel( newView ),
			nActionLabel: this.getNegativeActionLabel( newView ),
			formValid: false,
		}, () => {
			const valid = this.form.current?.isValid( false ) || false;
			this.setState( {
				formValid: valid,
			} );
		} );
	};
	//region Actions and Labels
	getPositiveActionLabel = ( view: ScreenView = this.state.view ) => {
		const { requireOtp } = this.state ?? {};
		switch ( view ) {
			case "services":
				return Strings.next;
			case "service_info":
				return Strings.save;
			case "terms":
				return requireOtp ? Strings.next : Strings.gl_subscribe;
			case "otp":
				return Strings.gl_verify_and_subscribe;
		}
	};
	getNegativeActionLabel = ( view: ScreenView = this.state.view ) => {
		switch ( view ) {
			case "services":
				return Strings.close;
			case "service_info":
				return Strings.back;
			case "terms":
				return Strings.back;
			case "otp":
				return Strings.back;
		}
	};
	onNegativeAction = () => {
		const { view, nActionLabel } = this.state;
		if ( nActionLabel === Strings.close ) {
			JQModal( "GreenListPopup" ).hide();
			return;
		}
		switch ( view ) {
			case "services":
				return Strings.close;
			case "service_info":
				return this.switchView( "services" );
			case "terms":
				return this.switchView( "services" );
			case "otp":
				return this.switchView( "terms" );
		}
	};
	onPositiveAction = () => {
		const { view } = this.state;
		switch ( view ) {
			case "services":
				return this.switchView( "terms" );
			case "service_info":
				const data = ( this.form.current as SubscriptionDetailsForm ).getData();
				const ss: Partial<SubscriptionService> = {
					ToValue: data?.budget?.max,
					FromValue: data?.budget?.min,
					PropertyTypes: data?.propertyTypes?.map( pId => {
						return convertToGPropertyType( PropertyTypesItems.find( x => x.Id === pId )! );
					} ),
					Areas: data?.areas?.map( aId => {
						return {
							AreaId: aId,
						};
					} )
				};
				const svc = {
					...this.state.tempSubscription,
					...ss,
				} as SubscriptionService;
				const subs = this.state.subscriptions.filter( x => x.ServiceTypeId !== svc.ServiceTypeId );
				this.setState( {
					subscriptions: [
						...subs,
						svc,
					],
				}, () => {
					this.switchView( "services" );
				} );
				return;
			case "terms":
				const termData = ( this.form.current as Terms ).getData();
				this.handleTermData( termData );
				return;
			case "otp":
				return this.onOTPAction();
		}
	};

	//endregion
	render() {
		const { view, subscriptions, tempSubscription } = this.state;
		const { pActionLabel, nActionLabel, contactInfo, otpRequirement } = this.state;
		const isOky = this.isOky();
		return (
			<Popup
				id={ "GreenListPopup" }
				modalMainContainer={ "modal fade" }
				modalSubContainer={ "modal-dialog modal-dialog-centered modal-lg" }
				modalBodyContainer={ "modal-body" }
				clickOutsideToDismiss={ false }
				title={ Strings.green_list } showIcon={ false }
                modalFooterContainer={ "modal-footer justify-content-center" }
                footer={<>
                    <button
                        type="button" className="btn btn_1 me-2" id="updateBtn"
                        disabled={!isOky} onClick={this.onPositiveAction}>
                        {pActionLabel}
                    </button>
					<button
						type="button" className={ "btn btn-grey" }
						id="resetBtn" onClick={ this.onNegativeAction }>
						{ nActionLabel }
					</button>
				</> }
				onDismiss={ this.props.onDismiss }
			>
				{ view === "services" &&
					<SubscriptionServicesForm
						subscriptions={ subscriptions }
						onUpdated={ this.onFormValidityChanged }
						onServiceRemoved={ this.onServiceRemoved }
						onServiceSelected={ this.onServiceSelected }
						onServiceDetails={ this.onServiceDetails }
						ref={ this.form as React.RefObject<SubscriptionServicesForm> }/>
				}
				{ view === "service_info" &&
					<SubscriptionDetailsForm
						service={ tempSubscription! }
						onUpdated={ this.onFormValidityChanged }
						ref={ this.form as React.RefObject<SubscriptionDetailsForm> }/>
				}
				{ view === "terms" &&
					<Terms
						info={ contactInfo }
						onUpdated={ this.onFormValidityTermChanged }
						ref={ this.form as React.RefObject<Terms> }/>
				}
				{ view === "otp" &&
					<Otp
						{ ...otpRequirement }
						onUpdated={ this.onFormValidityTermChanged }
						ref={ this.form as React.RefObject<Otp> }/>
				}
			</Popup>
		);
	}

	//region Generic FormCallbacks
	onFormValidityChanged = ( isValid: boolean, _data?: any ) => {
		this.setState( {
			formValid: isValid,
		} );
	};
	onFormValidityTermChanged = ( isValid: boolean, data?: ContactInfo ) => {
		this.setState( {
			formValid: isValid,
			requireOtp: this.requireOTP( data ?? {} ) != null,
		}, () => {
			this.setState( {
				pActionLabel: this.getPositiveActionLabel(),
			} );
		} );
	};
	//endregion

	//region SubscriptionService callbacks
	onServiceSelected = ( sub: SubscriptionService ) => {
		if ( sub.ServiceTypeId === SubscriptionServiceId.ALL ) {
			this.setState( {
				subscriptions: [ sub ]
			} );
		} else {
			this.setState( {
				tempSubscription: sub,
			}, () => {
				this.switchView( "service_info" );
			} );
		}
	};
	onServiceDetails = ( sub: SubscriptionService ) => {
		if ( sub.ServiceTypeId === SubscriptionServiceId.ALL ) {

		} else {
			this.setState( {
				tempSubscription: this.state.subscriptions.find( x => x.ServiceTypeId === sub.ServiceTypeId ),
			}, () => {
				this.switchView( "service_info" );
			} );
		}
	};
	onServiceRemoved = ( sub: SubscriptionService ) => {
		this.setState( {
			subscriptions: this.state.subscriptions.filter( x => x.ServiceTypeId !== sub.ServiceTypeId ),
		} );
	};
	//endregion

	//region Terms Handler
	requireOTP = ( termData: ContactInfo ) => {
		const { mode, subscription } = this.props;
		const oEmail = mode === "SUBSCRIBE" ? getEmail() : subscription?.Email;
		const oMobile = mode === "SUBSCRIBE" ? normaliseMobileNumber( getMobileNumber() ?? "" ) :
			normaliseMobileNumber( subscription?.Mobile ?? "" );
		let r: ContactInfo = {};
		let required = false;
		if ( termData.email !== oEmail ) {
			required = true;
			r.email = termData.email;
		}
		if ( normaliseMobileNumber( termData.mobile ?? "" ) !== oMobile ) {
			required = true;
			r.mobile = termData.mobile;
		}
		if ( required ) return r;
		return undefined;
	};
	handleTermData = ( termData: ContactInfo ) => {
		const requireOtp = this.requireOTP( termData );
		if ( requireOtp ) {
			this.setState( {
				contactInfo: termData,
				otpRequirement: requireOtp,
			}, () => {
				this.switchView( "otp" );
			} );
		} else {
			this.setState( {
				contactInfo: termData,
			}, this.subscribeToGreenList );
		}
	};
	onOTPAction = () => {
		( this.form.current as Otp ).verifyOTP().then( x => {
			if ( x ) this.subscribeToGreenList();
		} );
	};
	//endregion
	subscribeToGreenList = () => {
		const glSub: GreenListStatus = {
			ServiceTypes: this.state.subscriptions,
			TotalRowsCount: 1,
			Mobile: this.state.contactInfo?.mobile,
			Email: this.state.contactInfo?.email,
			ExpiryDate: new Date().asServerString(),
			NameAr: getName()?.ar,
			NameEn: getName()?.en,
			Notes: "",
			IsActive: true,
			ParticipantId: getParticipantID(),
		};
		this.props.onSubscriptionUpdated( glSub ).catch( e => {
			showErrorMessage( e );
		} );
	};
}

export default GreenListPopup;
